import { call, put, takeEvery, all, takeLatest, select } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import axios from 'axios';
import _ from "../../baseApp/shared/utils/ObjectUtils";
import {getEntityCall} from "../../baseApp/entities/sagas";
import {getVisitorCall} from "../visitors/sagas";
import DateTimeUtils from "../../baseApp/shared/utils/DateTimeUtils";
import { getUserPageRefresh } from '../../baseApp/auth/sagas';

const controllerUrl = '/visits/';

const searchVisitsCall = searchFields => new Promise((resolve, reject) => {
    if (!axios.defaults.headers.common['Authorization']) {
        axios.get(process.env.REACT_APP_BACKEND_URL + "/auth/read-session").then(response => {
            axios.defaults.headers.common["Authorization"] = "Bearer " + response.data;
            return axios.get(controllerUrl + 'search', {
                params: {
                    ...searchFields,
                }
            }).then(
                response => resolve(response.data),
                error => reject(error)
            );
        }).catch(e => console.log(e));
    }
    else{
    axios.get(controllerUrl + 'search', {
        params: {
            ...searchFields,
        }
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
    }
});

const checkOutCall = visitIds => new Promise((resolve, reject) => {
    axios.put(controllerUrl + "check-out", {
        visitId : visitIds
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const checkInCall = visitIds => new Promise((resolve, reject) => {
    axios.put(controllerUrl + "check-in", {
        visitId : visitIds
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const createVisitCall = checkInData => new Promise((resolve, reject) => {
    axios.post(controllerUrl, checkInData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

function* searchVisitsRequest({searchFields}) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        if(searchFields.sortProperty === 'building.name') {
            searchFields.sortProperty = 'building';
        }

        const searchResults = yield call(() => searchVisitsCall(searchFields));
        const entityIds = searchResults.content.map(visit => visit.buildingId);
        const uniqueEntityIds = [...new Set(entityIds)];
        const entities = yield all(
            uniqueEntityIds
                .filter(id => !_.isNil(id))
                .map(id => call(getEntityCall, id))
        );

        const visitorIds = searchResults.content.map(visit => visit.visitor);
        const uniqueVisitorIds = [...new Set(visitorIds)];
        const visitors = yield all(
            uniqueVisitorIds
                .filter(id => !_.isNil(id))
                .map(id => call(getVisitorCall, id))
        );
        
        const searchResultsWithEntitiesAndVisitors = {
            ...searchResults,
            content: searchResults.content.map(
                visit => addFieldsToVisits(visit, visitors, entities)
            )
        };

        yield put({
            type: actionTypes.VISIT_SEARCH_SUCCESS,
            payload: searchResultsWithEntitiesAndVisitors
        });
    } catch (e) {
        yield put({
            type: actionTypes.VISIT_SEARCH_FAILED
        });
    }
}

function* getLastVisitorVisitsRequest({visitorId}) {
    try {
        const searchFields = {
            visitor : visitorId,
            pageSize: 15,
        };

        const searchResults = yield call(searchVisitsCall, searchFields);

        const entityIds = searchResults.content.map(visit => visit.buildingId);
        const uniqueEntityIds = [...new Set(entityIds)];
        const entities = yield all(
            uniqueEntityIds
                .filter(id => !_.isNil(id))
                .map(id => call(getEntityCall, id))
        );

        const visitorIds = searchResults.content.map(visit => visit.visitor);
        const uniqueVisitorIds = [...new Set(visitorIds)];
        const visitors = yield all(
            uniqueVisitorIds
                .filter(id => !_.isNil(id))
                .map(id => call(getVisitorCall, id))
        );

        const searchResultsWithEntitiesAndVisitors = {
            ...searchResults,
            content: searchResults.content.map(
                visit => addFieldsToVisits(visit, visitors, entities)
            )
        };

        yield put({
            type: actionTypes.VISITOR_LAST_VISITS_SUCCESS,
            payload: searchResultsWithEntitiesAndVisitors
        });
    } catch (e) {
        yield put({
            type: actionTypes.VISITOR_LAST_VISITS_FAILED
        });
    }
}

function* visitsCheckOutRequest({visitIds}) {
    try {
        const idsToCheckout = Object
            .entries(visitIds)
            .filter(([key, value]) => value)
            .reduce((acc, [key]) => {
                return [
                    ...acc,
                    key
                ]
            }, []);
        yield call(checkOutCall, idsToCheckout);

        yield put({
            type: actionTypes.VISIT_CHECK_OUT_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.VISIT_CHECK_OUT_FAILED
        })
    }
}

function* visitsCheckInRequest({ visitIds }) {
    try {
        const idsToCheckIn = Object
            .entries(visitIds)
            .filter(([key, value]) => value)
            .reduce((acc, [key]) => {
                return [
                    ...acc,
                    key
                ]
            }, []);

        yield call(checkInCall, idsToCheckIn);

        yield put({
            type: actionTypes.VISIT_CHECK_IN_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.VISIT_CHECK_IN_FAILED
        })
    }
}

function* createCheckIn({ checkInData }) {
    try {
        yield call(createVisitCall, checkInData);

        yield put({
            type: actionTypes.CREATE_VISIT_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.CREATE_VISIT_FAILED
        })
    }
}

function addFieldsToVisits(visit, visitors, entities) {
    const checkInDate = visit.checkInTimestamp ? 
        DateTimeUtils.getDateFromUnix(visit.checkInTimestamp) :
        DateTimeUtils.getDateFromUnix(visit.expectedCheckInTimestamp);
    const checkInTime = visit.checkInTimestamp ? 
        DateTimeUtils.getTimeWithoutSecondsFromUnix(visit.checkInTimestamp) :
        DateTimeUtils.getTimeWithoutSecondsFromUnix(visit.expectedCheckInTimestamp);
    const checkOutTime = visit.checkOutTimestamp ?
        DateTimeUtils.getTimeWithoutSecondsFromUnix(visit.checkOutTimestamp) :
        "";
    const checkInCheckOutString = checkInTime + ' - ' + checkOutTime;

    return {
        ...visit,
        entity: entities.find(e => e.id === visit.buildingId),
        visitor: visitors.find(e => e.id === visit.visitor),
        checkInDate,
        checkInTime,
        checkOutTime,
        checkInOutTime: checkInCheckOutString,
        status: !visit.checkOutTimestamp,
        checkInOut: checkInDate + "  (" + checkInCheckOutString + ")"
    }
}

const visitsSagas = [
    takeLatest(actionTypes.VISIT_SEARCH_REQUEST, searchVisitsRequest),
    takeEvery(actionTypes.VISIT_CHECK_OUT_REQUEST, visitsCheckOutRequest),
    takeLatest(actionTypes.VISIT_CHECK_IN_REQUEST, visitsCheckInRequest),
    takeEvery(actionTypes.VISITOR_LAST_VISITS_REQUEST, getLastVisitorVisitsRequest),
    takeLatest(actionTypes.CREATE_VISIT_REQUEST, createCheckIn)
];

export {
    visitsSagas,
};